import { useCallback, useEffect, useState } from "react";
import { MessageSquare, User } from "lucide-react";
import { useMemberAuth } from "../../Database/RekebishaMembers/MemberAuthContext";
import { reviewApiFor } from "../../Database/RekebishaMembers/reviews";
import { fetchReplies } from "../../Database/RekebishaAdmin/reviewReplies";
import StarRating from "../StarRating";
import GoogleSignInButton from "../GoogleSignInButton";

const formatDate = (millis) => {
  if (!millis) return "";
  return new Date(millis).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

/**
 * Review list (always public, matching the app) plus a composer that's gated behind
 * Google sign-in — the website equivalent of the review section on HandymanDetail.js /
 * AgencyDetail.js in the app, reading and writing the exact same collections.
 */
const ReviewSection = ({ subjectType, subjectId, subjectName = "this listing" }) => {
  const { user, signIn, isSigningIn } = useMemberAuth();
  const api = reviewApiFor(subjectType);

  const [reviews, setReviews] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [replies, setReplies] = useState({});
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  // Only handymen can reply to their own reviews (agencies have no equivalent "owner
  // reply" concept), and replies live in the fundi's own project, not the reviews one.
  const load = useCallback(async () => {
    if (!subjectId) return;
    try {
      const [all, mine, theirReplies] = await Promise.all([
        api.fetchReviews(subjectId),
        api.fetchMyReview(subjectId),
        subjectType === "handyman"
          ? fetchReplies(subjectId).catch((e) => {
              console.warn("Could not load review replies:", e);
              return {};
            })
          : Promise.resolve({}),
      ]);
      setReviews(all);
      setMyReview(mine);
      setReplies(theirReplies);
      if (mine) {
        setScore(mine.rating);
        setComment(mine.comment);
      }
    } catch (e) {
      console.warn("Could not load reviews:", e);
    } finally {
      setIsLoading(false);
    }
  }, [api, subjectId, subjectType]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId, user]);

  const count = reviews.length;
  const average = count > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / count : null;

  const handleSignIn = async () => {
    setError(null);
    const { error: signInError } = await signIn();
    if (signInError) setError(signInError);
  };

  const onSubmit = async () => {
    if (score < 1) {
      setError("Tap a star to rate before submitting.");
      return;
    }

    setError(null);
    setStatus(null);
    setIsSubmitting(true);
    try {
      await api.submitReview({ subjectId, rating: score, comment });
      await load();
      setStatus(myReview ? "Your review has been updated." : "Thanks for the review!");
    } catch (e) {
      setError(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-6 sm:p-8">
      <div className="flex items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-bold text-navy">Reviews</h2>
        {average !== null && (
          <div className="flex items-center gap-2 bg-brandgreen/10 text-brandgreen px-3 py-1.5 rounded-full">
            <StarRating value={average} size={14} />
            <span className="text-sm font-bold">
              {average.toFixed(1)} · {count}
            </span>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-b border-gray-100 pb-6 mb-6">
        {user ? (
          <>
            <p className="text-sm font-semibold text-navy mb-1">
              {myReview ? "Your review" : `Rate ${subjectName}`}
            </p>
            <p className="text-xs text-gray-500 mb-3">
              {myReview ? "You can update your rating at any time." : "Your rating helps other members choose."}
            </p>
            <StarRating value={score} onChange={setScore} size={28} className="mb-3" />
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={600}
              rows={3}
              placeholder="Describe your experience (optional)"
              className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brandgreen resize-none"
            />
            <button
              type="button"
              onClick={onSubmit}
              disabled={isSubmitting}
              className="mt-3 bg-brandgreen text-white py-2.5 px-6 rounded-lg hover:bg-brandgreen-dark transition-colors text-sm font-semibold disabled:opacity-60"
            >
              {isSubmitting ? "Saving…" : myReview ? "Update review" : "Submit review"}
            </button>
            {status && <p className="text-xs text-brandgreen mt-2">{status}</p>}
          </>
        ) : (
          <div className="text-center py-4">
            <MessageSquare className="w-8 h-8 text-brandgreen/60 mx-auto mb-3" />
            <p className="text-sm text-gray-600 mb-4">Sign in with Google to leave a review.</p>
            <div className="max-w-xs mx-auto">
              <GoogleSignInButton onClick={handleSignIn} loading={isSigningIn} />
            </div>
          </div>
        )}
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brandgreen"></div>
        </div>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-gray-500">No one has reviewed {subjectName} yet.</p>
      ) : (
        <div className="space-y-5">
          {reviews.map((review, index) => (
            <div key={review.id} className={index > 0 ? "pt-5 border-t border-gray-100" : ""}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-navy flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-navy truncate">
                      {review.userName}
                      {myReview?.id === review.id ? " (you)" : ""}
                    </p>
                    <p className="text-xs text-gray-400 flex-shrink-0">{formatDate(review.createdAtMillis)}</p>
                  </div>
                  <StarRating value={review.rating} size={13} className="mt-1" />
                  {!!review.comment && <p className="text-sm text-gray-600 mt-2">{review.comment}</p>}

                  {!!replies[review.id] && (
                    <div className="mt-2 pl-3 border-l-2 border-brandgreen">
                      <p className="text-xs font-bold text-brandgreen">
                        {subjectName.split(" ")[0]}'s reply
                      </p>
                      <p className="text-sm text-gray-600 mt-0.5">{replies[review.id].text}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewSection;
